import React, { useState, useEffect, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import {
  TextField,
  Box,
  Typography,
  Grid,
  CircularProgress,
  Chip,
  InputAdornment,
  IconButton,
  Dialog,
  DialogContent,
  DialogActions,
  Tooltip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  InputBase,
  Backdrop,
  Fade,
} from "@mui/material";
import {
  Description as DescriptionIcon,
  InsertDriveFile as InsertDriveFileIcon,
  Search as SearchIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  CloudUpload as CloudUploadIcon,
} from '@mui/icons-material';
import { X, Check, Upload, FilePlus2 } from 'lucide-react';
import { useTranslation } from "react-i18next";
import { backendClient } from "../../../../api/backend";
import { fetchContractCategories } from "../../../../redux/actions/contractAction";
import { useSearchTokens } from "../../../SearchScreen/searchDesignTokens";

const MyTemplatesOption = ({
  contractName,
  setContractName,
  description,
  setDescription,
  error,
  selectedTemplate,
  setSelectedTemplate,
  selectedCountry,
  setSelectedCountry,
  onCountryChange,
  onSaveAsTemplate,
  onNavigateToTemplate,
}) => {
  const { t, i18n } = useTranslation();
  const dk = useSearchTokens();
  const dispatch = useDispatch();
  const currentLanguage = i18n.language?.split('-')[0] || 'fr';
  const getTemplateName = (name) => {
    if (!name) return '';
    if (typeof name === 'string') return name;
    return name[currentLanguage] || Object.values(name)[0] || '';
  };
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("");
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [currentTemplate, setCurrentTemplate] = useState(null);
  const [templateName, setTemplateName] = useState("");
  const [templateDescription, setTemplateDescription] = useState("");
  const [templateContent, setTemplateContent] = useState("");
  const [templateCategory, setTemplateCategory] = useState("");
  const [createTab, setCreateTab] = useState('import');
  const [importedFile, setImportedFile] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [creating, setCreating] = useState(false);
  const inputRef = useRef(null);

  const { contractCategories } = useSelector(state => state.contract);

  useEffect(() => {
    if (contractCategories && contractCategories.length === 0) {
      dispatch(fetchContractCategories());
    }
  }, [dispatch, contractCategories]);

  const categoryOptions = (contractCategories || []).map(category => ({
    value: category,
    label: t(`contract_categories.${category}`, { defaultValue: category }),
  }));

  const supportedFormats = [
    { value: 'docx', label: 'Word (.docx)' },
    { value: 'pdf', label: 'PDF (.pdf)' },
    { value: 'txt', label: 'Text (.txt)' },
  ];

  useEffect(() => { fetchUserTemplates(); }, []);

  const fetchUserTemplates = async () => {
    try {
      setLoading(true);
      const data = await backendClient.getUserTemplates();
      setTemplates(data);
    } catch (err) {
      console.error("Failed to fetch user templates:", err);
    } finally {
      setLoading(false);
    }
  };

  const filteredTemplates = templates.filter(template => {
    const name = getTemplateName(template.name);
    const matchesSearch = name.toLowerCase().includes((searchTerm || '').toLowerCase());
    const matchesCategory = !categoryFilter || template.category === categoryFilter;
    return matchesSearch && matchesCategory;
  });

  const handleCreateTemplate = async () => {
    if (!templateName.trim()) return;
    try {
      setCreating(true);
      let createdTemplate;
      if (createTab === 'import' && importedFile) {
        createdTemplate = await backendClient.createTemplateFromImport(importedFile, {
          name: templateName.trim(),
          description: templateDescription.trim() || null,
          category: templateCategory || null,
        });
      } else {
        createdTemplate = await backendClient.createTemplate(
          { [currentLanguage]: templateName.trim() },
          templateDescription.trim() ? { [currentLanguage]: templateDescription.trim() } : null,
          templateContent.trim(),
          templateCategory || null
        );
      }
      setIsCreateDialogOpen(false);
      resetTemplateForm();
      if (createdTemplate?.id && onNavigateToTemplate) {
        onNavigateToTemplate(createdTemplate.id);
      } else {
        fetchUserTemplates();
      }
    } catch (err) {
      console.error("Failed to create template:", err);
    } finally {
      setCreating(false);
    }
  };

  const handleUpdateTemplate = async () => {
    if (!currentTemplate) return;
    try {
      await backendClient.updateTemplate(currentTemplate.id, {
        name: { [currentLanguage]: templateName },
        description: templateDescription ? { [currentLanguage]: templateDescription } : null,
        content: templateContent,
        category: templateCategory || null,
      });
      setIsEditDialogOpen(false);
      fetchUserTemplates();
      resetTemplateForm();
    } catch (err) {
      console.error("Failed to update template:", err);
    }
  };

  const handleDeleteTemplate = async (templateId) => {
    if (!window.confirm(t("confirm_delete_template") || "Are you sure you want to delete this template?")) return;
    try {
      await backendClient.deleteTemplate(templateId);
      fetchUserTemplates();
      if (selectedTemplate?.id === templateId) setSelectedTemplate(null);
    } catch (err) {
      console.error("Failed to delete template:", err);
    }
  };

  const resetTemplateForm = () => {
    setTemplateName("");
    setTemplateDescription("");
    setTemplateContent("");
    setTemplateCategory("");
    setCurrentTemplate(null);
    setImportedFile(null);
    setCreateTab('import');
    if (inputRef.current) inputRef.current.value = "";
  };

  const openCreateDialog = () => { setTemplateContent(onSaveAsTemplate?.() || ""); setIsCreateDialogOpen(true); };
  const openEditDialog = (template) => {
    setCurrentTemplate(template);
    setTemplateName(getTemplateName(template.name));
    setTemplateDescription(getTemplateName(template.description) || "");
    setTemplateContent(template.content || "");
    setTemplateCategory(template.category || "");
    setIsEditDialogOpen(true);
  };

  const getAcceptedFileTypes = () => supportedFormats.map(f => `.${f.value}`).join(',');

  const validateFile = (file) => {
    if (!file) return false;
    const ext = file.name.split('.').pop().toLowerCase();
    return supportedFormats.some(f => f.value === ext);
  };

  const processFile = (file) => {
    if (validateFile(file)) {
      if (!templateName) setTemplateName(file.name.replace(/\.[^/.]+$/, ""));
      setImportedFile(file);
      return true;
    }
    return false;
  };

  const handleFileImport = (e) => { const file = e.target.files[0]; if (file) processFile(file); };
  const handleRemoveFile = () => { setImportedFile(null); if (inputRef.current) inputRef.current.value = ""; };
  const handleDragEnter = (e) => { e.preventDefault(); e.stopPropagation(); if (!creating) setIsDragging(true); };
  const handleDragOver  = (e) => { e.preventDefault(); e.stopPropagation(); if (!creating) setIsDragging(true); };
  const handleDragLeave = (e) => { e.preventDefault(); e.stopPropagation(); setIsDragging(false); };
  const handleDrop = (e) => {
    e.preventDefault(); e.stopPropagation(); setIsDragging(false);
    if (creating) return;
    if (e.dataTransfer.files?.length > 0) { const file = e.dataTransfer.files[0]; if (processFile(file)) setCreateTab('import'); }
  };
  const triggerFileInput = () => { if (inputRef.current) inputRef.current.click(); };

  // ── Shared sx helpers ──────────────────────────────────────────────────────

  const selectSx = {
    '& .MuiOutlinedInput-root': {
      fontSize: '0.82rem',
      backgroundColor: dk.inputBg,
      borderRadius: '8px',
      '& fieldset': { borderColor: dk.border },
      '&:hover fieldset': { borderColor: dk.borderHover },
      '&.Mui-focused fieldset': { borderColor: dk.goldBdr, borderWidth: 1 },
    },
    '& .MuiInputLabel-root': {
      color: dk.text3,
      fontSize: '0.8rem',
      '&.Mui-focused': { color: dk.gold },
    },
    '& .MuiSelect-select': { color: dk.text1 },
    '& .MuiSvgIcon-root': { color: dk.text3 },
  };

  const textFieldSx = {
    '& .MuiOutlinedInput-root': {
      fontSize: '0.82rem',
      backgroundColor: dk.inputBg,
      borderRadius: '8px',
      color: dk.text1,
      '& fieldset': { borderColor: dk.border },
      '&:hover fieldset': { borderColor: dk.borderHover },
      '&.Mui-focused fieldset': { borderColor: dk.goldBdr, borderWidth: 1 },
    },
    '& .MuiInputLabel-root': {
      color: dk.text3,
      fontSize: '0.8rem',
      '&.Mui-focused': { color: dk.gold },
    },
    '& .MuiInputBase-input': { color: dk.text1 },
    '& .MuiInputAdornment-root .MuiSvgIcon-root': { color: dk.text3, fontSize: '1.1rem' },
    '& .MuiFormHelperText-root': { fontSize: '0.7rem' },
  };

  const sectionLabel = {
    fontSize: '0.7rem',
    fontWeight: 600,
    color: dk.text3,
    letterSpacing: '0.06em',
    textTransform: 'uppercase',
    mb: 1,
  };

  // ── Tab chip for inner dialog ──────────────────────────────────────────────
  const innerChipSx = (selected) => ({
    backgroundColor: selected ? dk.chipBgActive : dk.chipBg,
    color: selected ? dk.chipTextActive : dk.chipText,
    height: 24,
    borderRadius: '5px',
    cursor: 'pointer',
    '& .MuiChip-label': { fontSize: '0.7rem', padding: '0 10px', fontWeight: selected ? 600 : 400 },
    '&:hover': { backgroundColor: selected ? dk.chipBgActive : dk.hover },
  });

  // ── Inner create/edit dialog ───────────────────────────────────────────────
  const renderTemplateDialog = (isEdit = false) => (
    <Dialog
      open={isEdit ? isEditDialogOpen : isCreateDialogOpen}
      onClose={() => {
        isEdit ? setIsEditDialogOpen(false) : setIsCreateDialogOpen(false);
        resetTemplateForm();
      }}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          backgroundColor: dk.surface,
          border: `1px solid ${dk.goldBdr}`,
          borderRadius: '12px',
          boxShadow: '0 24px 60px rgba(0,0,0,0.8)',
        },
      }}
      slotProps={{
        backdrop: {
          sx: { backgroundColor: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(8px)' },
        },
      }}
    >
      {/* Inner dialog header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 2.5,
          py: 1.5,
          borderBottom: `1px solid ${dk.border}`,
          background: `linear-gradient(180deg, ${dk.elevated} 0%, ${dk.surface} 100%)`,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Box
            sx={{
              width: 26,
              height: 26,
              borderRadius: '7px',
              backgroundColor: dk.goldSub,
              border: `1px solid ${dk.goldBdr}`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <FilePlus2 size={13} color={dk.gold} />
          </Box>
          <Typography
            sx={{
              color: dk.text1,
              fontFamily: '"Cormorant Garamond", Georgia, serif',
              fontSize: '0.95rem',
              fontWeight: 500,
            }}
          >
            {isEdit ? t("edit_template") || "Edit Template" : t("create_template") || "Create Template"}
          </Typography>
        </Box>
        <IconButton
          onClick={() => { isEdit ? setIsEditDialogOpen(false) : setIsCreateDialogOpen(false); resetTemplateForm(); }}
          size="small"
          sx={{ color: dk.text3, width: 26, height: 26, '&:hover': { color: dk.text1, backgroundColor: dk.hover } }}
        >
          <X size={14} />
        </IconButton>
      </Box>

      <DialogContent sx={{ p: 2.5, backgroundColor: dk.surface }}>
        {/* Tab row (create only) */}
        {!isEdit && (
          <Box sx={{ display: 'flex', gap: 0.75, mb: 2 }}>
            <Chip label={t("import_document") || "Import"} onClick={() => setCreateTab('import')} clickable sx={innerChipSx(createTab === 'import')} />
            <Chip label={t("manual_input") || "Manual"} onClick={() => setCreateTab('manual')} clickable sx={innerChipSx(createTab === 'manual')} />
          </Box>
        )}

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          <TextField
            fullWidth
            size="small"
            label={t("template_name") || "Template Name"}
            value={templateName}
            onChange={(e) => setTemplateName(e.target.value)}
            sx={textFieldSx}
          />
          <TextField
            fullWidth
            size="small"
            label={t("template_description") || "Description (Optional)"}
            value={templateDescription}
            onChange={(e) => setTemplateDescription(e.target.value)}
            multiline
            rows={2}
            sx={textFieldSx}
          />
          <FormControl fullWidth size="small" sx={selectSx}>
            <InputLabel>{t("template_category") || "Category (Optional)"}</InputLabel>
            <Select
              value={templateCategory}
              onChange={(e) => setTemplateCategory(e.target.value)}
              label={t("template_category") || "Category (Optional)"}
            >
              <MenuItem value=""><em>{t("no_category") || "No category"}</em></MenuItem>
              {categoryOptions.map((c) => (
                <MenuItem key={c.value} value={c.value}>{c.label}</MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Content: file upload or manual */}
          {isEdit || createTab === 'manual' ? (
            <TextField
              fullWidth
              size="small"
              label={t("template_content") || "Template Content"}
              value={templateContent}
              onChange={(e) => setTemplateContent(e.target.value)}
              multiline
              rows={8}
              sx={textFieldSx}
            />
          ) : (
            <Box
              sx={{
                border: `1px dashed ${isDragging ? dk.gold : dk.borderHover}`,
                borderRadius: '8px',
                p: 2.5,
                textAlign: 'center',
                backgroundColor: isDragging ? dk.goldSub : dk.elevated,
                cursor: creating ? 'not-allowed' : 'pointer',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                minHeight: 140,
                transition: 'all 0.18s ease',
                '&:hover': !creating ? { borderColor: dk.gold, backgroundColor: dk.goldSub } : {},
              }}
              onClick={!creating ? triggerFileInput : undefined}
              onDragEnter={handleDragEnter}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
            >
              <input
                type="file"
                ref={inputRef}
                style={{ display: 'none' }}
                accept={getAcceptedFileTypes()}
                onChange={handleFileImport}
                disabled={creating}
              />
              {importedFile ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, p: 1, borderRadius: '8px', backgroundColor: dk.goldSub, border: `1px solid ${dk.goldBdr}`, width: '100%' }}>
                  <InsertDriveFileIcon sx={{ color: dk.gold, fontSize: 20, flexShrink: 0 }} />
                  <Typography sx={{ fontSize: '0.82rem', fontWeight: 500, color: dk.text1, flexGrow: 1 }} noWrap>
                    {importedFile.name}
                  </Typography>
                  <IconButton
                    size="small"
                    onClick={(e) => { e.stopPropagation(); handleRemoveFile(); }}
                    disabled={creating}
                    sx={{ color: dk.text3, '&:hover': { color: dk.error, backgroundColor: 'rgba(248,113,113,0.08)' } }}
                  >
                    <X size={13} />
                  </IconButton>
                </Box>
              ) : (
                <>
                  <Upload size={22} color={isDragging ? dk.gold : dk.text3} style={{ marginBottom: 8 }} />
                  <Typography sx={{ fontSize: '0.82rem', fontWeight: 500, color: isDragging ? dk.gold : dk.text2, mb: 0.5 }}>
                    {isDragging ? t("drop_file_here") || "Drop here" : t("drag_drop_or_click") || "Drag & drop or click"}
                  </Typography>
                  <Typography sx={{ fontSize: '0.7rem', color: dk.text3 }}>
                    {supportedFormats.map(f => f.label).join(' · ')}
                  </Typography>
                </>
              )}
            </Box>
          )}
        </Box>
      </DialogContent>

      {/* Inner dialog footer */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 2.5,
          py: 1.5,
          borderTop: `1px solid ${dk.border}`,
          backgroundColor: dk.bg,
        }}
      >
        <Box
          onClick={() => { isEdit ? setIsEditDialogOpen(false) : setIsCreateDialogOpen(false); resetTemplateForm(); }}
          sx={{
            cursor: 'pointer',
            py: 0.5,
            px: 1,
            borderRadius: '6px',
            transition: 'all 0.15s',
            '&:hover': { backgroundColor: dk.hover },
          }}
        >
          <Typography sx={{ fontSize: '0.72rem', color: dk.text3, fontWeight: 500 }}>
            {t("cancel") || "Cancel"}
          </Typography>
        </Box>

        <Box
          onClick={
            !creating &&
            templateName.trim() &&
            (isEdit ? true : createTab === 'manual' ? templateContent.trim() : importedFile)
              ? (isEdit ? handleUpdateTemplate : handleCreateTemplate)
              : undefined
          }
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.75,
            cursor:
              creating || !templateName.trim() ||
              (!isEdit && createTab === 'manual' && !templateContent.trim()) ||
              (!isEdit && createTab === 'import' && !importedFile)
                ? 'default'
                : 'pointer',
            py: 0.65,
            px: 2,
            borderRadius: '8px',
            backgroundColor:
              creating || !templateName.trim() ||
              (!isEdit && createTab === 'manual' && !templateContent.trim()) ||
              (!isEdit && createTab === 'import' && !importedFile)
                ? dk.chipBg
                : dk.gold,
            color:
              creating || !templateName.trim() ||
              (!isEdit && createTab === 'manual' && !templateContent.trim()) ||
              (!isEdit && createTab === 'import' && !importedFile)
                ? dk.text3
                : '#000',
            transition: 'all 0.18s ease',
          }}
        >
          {creating && <CircularProgress size={11} sx={{ color: 'inherit' }} />}
          <Typography sx={{ fontSize: '0.74rem', fontWeight: 600, letterSpacing: '0.02em' }}>
            {creating
              ? (t("creating") || "Creating...")
              : isEdit
                ? (t("save") || "Save")
                : (t("create") || "Create")}
          </Typography>
        </Box>
      </Box>
    </Dialog>
  );

  // ── Main render ────────────────────────────────────────────────────────────

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', gap: 2 }}>

      {/* Top bar: title + create button */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Typography
          sx={{
            fontFamily: '"Cormorant Garamond", Georgia, serif',
            fontSize: '1.1rem',
            fontWeight: 500,
            color: dk.text1,
          }}
        >
          {t("my_templates") || "My Templates"}
        </Typography>
        <Box
          onClick={openCreateDialog}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.5,
            cursor: 'pointer',
            py: 0.5,
            px: 1.25,
            borderRadius: '7px',
            border: `1px solid ${dk.goldBdr}`,
            backgroundColor: dk.goldSub,
            transition: 'all 0.15s',
            '&:hover': { backgroundColor: dk.goldHover },
          }}
        >
          <AddIcon sx={{ fontSize: 14, color: dk.gold }} />
          <Typography sx={{ fontSize: '0.72rem', fontWeight: 600, color: dk.gold }}>
            {t("create_template") || "New Template"}
          </Typography>
        </Box>
      </Box>

      {/* Search + category filter */}
      <Box sx={{ display: 'flex', gap: 1.5 }}>
        <TextField
          fullWidth
          size="small"
          placeholder={t("search_templates") || "Search templates…"}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
          sx={textFieldSx}
        />
        <FormControl size="small" sx={{ minWidth: 170, ...selectSx }}>
          <InputLabel>{t("filter_by_category") || "Category"}</InputLabel>
          <Select
            value={categoryFilter}
            onChange={(e) => setCategoryFilter(e.target.value)}
            label={t("filter_by_category") || "Category"}
          >
            <MenuItem value=""><em>{t("all_categories") || "All"}</em></MenuItem>
            {categoryOptions.map((c) => (
              <MenuItem key={c.value} value={c.value}>{c.label}</MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      {/* Template grid */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 6 }}>
          <CircularProgress size={22} sx={{ color: dk.gold }} />
        </Box>
      ) : filteredTemplates.length === 0 ? (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            py: 4,
            px: 2,
            textAlign: 'center',
            borderRadius: '10px',
            border: `1px dashed ${dk.border}`,
            backgroundColor: dk.elevated,
          }}
        >
          <Box
            sx={{
              width: 36,
              height: 36,
              borderRadius: '9px',
              backgroundColor: dk.goldSub,
              border: `1px solid ${dk.goldBdr}`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mb: 1,
            }}
          >
            <DescriptionIcon sx={{ fontSize: 16, color: dk.gold }} />
          </Box>
          <Typography sx={{ fontSize: '0.82rem', fontWeight: 500, color: dk.text2, mb: 0.5 }}>
            {t("no_templates") || "No templates available"}
          </Typography>
          <Typography sx={{ fontSize: '0.72rem', color: dk.text3 }}>
            {t("create_first_template") || "Create your first template"}
          </Typography>
        </Box>
      ) : (
        <Grid container spacing={1.5} sx={{ flexGrow: 1, overflowY: 'auto', alignContent: 'flex-start' }}>
          {filteredTemplates.map((template) => {
            const tName = getTemplateName(template.name);
            const isSelected = selectedTemplate?.id === template.id;

            return (
              <Grid item xs={12} sm={6} md={4} key={template.id}>
                <Box
                  onClick={() => setSelectedTemplate(isSelected ? null : template)}
                  sx={{
                    p: 1.75,
                    borderRadius: '10px',
                    border: `1px solid ${isSelected ? dk.goldBdr : dk.border}`,
                    backgroundColor: isSelected ? dk.goldSub : dk.elevated,
                    cursor: 'pointer',
                    position: 'relative',
                    transition: 'all 0.18s ease',
                    '&:hover': {
                      borderColor: isSelected ? dk.goldBdr : dk.borderHover,
                      backgroundColor: isSelected ? dk.goldSub : dk.hover,
                    },
                  }}
                >
                  {/* Selected indicator */}
                  {isSelected && (
                    <Box
                      sx={{
                        position: 'absolute',
                        top: 8,
                        right: 8,
                        width: 18,
                        height: 18,
                        borderRadius: '50%',
                        backgroundColor: dk.gold,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <Check size={10} color="#000" />
                    </Box>
                  )}

                  {/* Template name */}
                  <Typography
                    sx={{
                      fontSize: '0.82rem',
                      fontWeight: 500,
                      color: dk.text1,
                      mb: 0.75,
                      pr: isSelected ? 3 : 0,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {tName}
                  </Typography>

                  {/* Category + actions row */}
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    {template.category ? (
                      <Box
                        sx={{
                          display: 'inline-block',
                          px: 0.75,
                          py: '2px',
                          borderRadius: '4px',
                          backgroundColor: dk.chipBg,
                          fontSize: '0.64rem',
                          color: dk.text3,
                          fontWeight: 500,
                          letterSpacing: '0.03em',
                        }}
                      >
                        {t(`contract_categories.${template.category}`, { defaultValue: template.category })}
                      </Box>
                    ) : (
                      <Box />
                    )}

                    <Box sx={{ display: 'flex', gap: 0.25 }} onClick={(e) => e.stopPropagation()}>
                      <Tooltip title={t("edit") || "Edit"} placement="top">
                        <IconButton
                          size="small"
                          onClick={() => {
                            if (onNavigateToTemplate) {
                              onNavigateToTemplate(template.id);
                            } else {
                              openEditDialog(template);
                            }
                          }}
                          sx={{
                            color: dk.text3,
                            p: 0.4,
                            '&:hover': { color: dk.text1, backgroundColor: dk.hover },
                          }}
                        >
                          <EditIcon sx={{ fontSize: 13 }} />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title={t("delete") || "Delete"} placement="top">
                        <IconButton
                          size="small"
                          onClick={() => handleDeleteTemplate(template.id)}
                          sx={{
                            color: dk.text3,
                            p: 0.4,
                            '&:hover': { color: dk.error, backgroundColor: 'rgba(248,113,113,0.08)' },
                          }}
                        >
                          <DeleteIcon sx={{ fontSize: 13 }} />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </Box>
                </Box>
              </Grid>
            );
          })}
        </Grid>
      )}

      {/* Contract details */}
      <Box sx={{ mt: 'auto', pt: 1 }}>
        <Box sx={{ height: '1px', backgroundColor: dk.border, mb: 2 }} />
        <Typography sx={sectionLabel}>{t("contract_details") || "Contract Details"}</Typography>
        <TextField
          fullWidth
          size="small"
          label={t("contract_title") || "Contract Title"}
          value={contractName}
          onChange={(e) => setContractName(e.target.value)}
          error={!!error && !contractName.trim()}
          helperText={!contractName.trim() && error ? t("name_required") || "Contract name required" : ""}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <InsertDriveFileIcon />
              </InputAdornment>
            ),
          }}
          sx={{ ...textFieldSx, mb: 1.5 }}
        />
        <TextField
          fullWidth
          size="small"
          label={t("description_optional") || "Description (Optional)"}
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          multiline
          rows={2}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <DescriptionIcon />
              </InputAdornment>
            ),
          }}
          sx={textFieldSx}
        />
      </Box>

      {renderTemplateDialog(false)}
      {renderTemplateDialog(true)}
    </Box>
  );
};

export default MyTemplatesOption;
